<script setup lang="ts">
import { api } from '@/api';
import {
  type CreateOtherQaEquipmentDto,
  CreateOtherQaEquipmentDtoTypeEnum,
  type CreateQaEquipmentRtSystemDto,
  QaEquipmentSubCategory,
  RtSystemManufacturer,
} from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import {
  getManufacturerLabel,
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import QaEquipmentForm from './components/QaEquipmentForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';

interface QaEquipmentAddViewProps {
  template?: string;
  subCategory?: QaEquipmentSubCategory;
}

const props = defineProps<QaEquipmentAddViewProps>();

const router = useRouter();
const { t } = useTranslation();
const notificationStore = useNotificationStore();
const qaEquipmentStore = useQaEquipmentStore();

const { action: loadEquipment, loading } = useLoading(async () => {
  if (props.template) {
    const { result } = await api.qaEquipmentApi.getQaEquipmentTemplate(
      {
        optionId: props.template,
      },
      [entityNotFoundErrorHandler(router)],
    );

    if (result) {
      if ('serialNumber' in result && !('dpin' in result)) {
        resetForm({
          values: {
            ...result,
            name: result.name || result.model || '',
            serialNumber: undefined,
          },
        });
      } else if ('serialNumber' in result && 'dpin' in result) {
        resetForm({
          values: {
            ...result,
            name: result.name || result.model || '',
            serialNumber: undefined,
            dpin: undefined,
          },
        });
      } else {
        resetForm({
          values: {
            ...result,
            name: result.name || result.model || '',
          },
        });
      }
    }
  } else if (props.subCategory) {
    const otherManufacturerInitialValues: CreateOtherQaEquipmentDto = {
      manufacturer: RtSystemManufacturer.MsOther,
      model: t('RtSystemModelEnum.OtherModel'),
      name: t('Untitled'),
      subCategory: props.subCategory,
      identification: '',
      isAvailable: true,
      $type:
        CreateOtherQaEquipmentDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDeviceComponentDtoCreateOtherQaEquipmentDtoPtwPtw100AppZeroApplicationShared,
    };
    resetForm({ values: otherManufacturerInitialValues });
  }
}, true);

onMounted(loadEquipment);

const { form } = useVeeForm<CreateQaEquipmentRtSystemDto>({
  schemaName: 'CreateQaEquipmentRtSystemDto',
  mode: 'add',
  customValidateFunctions: [
    backendValidator((qaEquipmentDto, errorHandlers) =>
      api.qaEquipmentApi.validateQaEquipmentRtSystem(
        {
          createQaEquipmentRtSystemDto: qaEquipmentDto,
        },
        errorHandlers,
      ),
    ),
  ],
});
const { handleSubmit, resetForm, values } = form;

const submit = handleSubmit(async (qaEquipmentRtSystem) => {
  const { result, success } = await api.qaEquipmentApi.createQaEquipmentRtSystem(
    {
      createQaEquipmentRtSystemDto: qaEquipmentRtSystem as CreateQaEquipmentRtSystemDto,
    },
    [formBackendErrorHandler(form)],
  );
  if (success && result?.id) {
    notificationStore.success(t('EntityAdded', { name: result.name, iuid: result.iuid }));
    qaEquipmentStore.setDeviceForRegistration(
      isRegistrablePtwQaEquipment(result) ? result : undefined,
    );
    await router.push({
      name: routenames.qaEquipment.add.configuration,
      params: { filter: 'withFilter' },
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle
          :title="values.name"
          :subtitle="`${t(getTypeLabel(values) as I18nKey)} | ${t(
            getManufacturerLabel(values) as I18nKey,
          )} | ${getModelLabel(values)}`"
        />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton
          :to="{
            name: routenames.qaEquipment.add.configuration,
            params: { filter: 'withFilter' },
          }"
        />
        <VeeSubmitButton class="ml-4" />
      </template>
      <QaEquipmentForm />
    </BasePage>
  </form>
</template>
